import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IPlanService } from "@shared/interfaces/plan-service.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { PlanKind } from "@shared/types/plans/enum/plan-kind.enum";
import { PlanStatus } from "@shared/types/plans/enum/plan-status.enum";
import { PlanDto, UpdatePlanDto } from "@shared/types/plans/plan.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, UseMutationResult, useQuery } from "react-query";
import ChangeUserModal from "./ChangePlanModal/ChangePlanModal";
import styles from "./Plans.module.scss";
import AlertModal from "./AlertModal/AlertModal";
import { PlanPlatform } from "@shared/types/plans/enum/plan-platform.enum";

const ALL = "All";

const defaultPlan: Partial<PlanDto> = {
  name: "",
  kind: PlanKind.TOKEN_PACK,
  revenueCatAppId: "",
  revenueCatProductId: "",
  reveuecatStoreIdentifier: "",
  description: null,
  tokens: 0,
  status: PlanStatus.ACTIVE,
  features: null,
};

const getPlatform = (type: PlanPlatform) => {
  switch (type) {
    case PlanPlatform.ANDROID: {
      return <Tag color="#8b0000">Google</Tag>;
    }

    case PlanPlatform.IOS: {
      return <Tag color="#9400d3">Apple</Tag>;
    }

    default:
      return null;
  }
};

const ALL_OBJECT = { label: "All", value: ALL };

export const Statuses = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export const Platforms = [
  { label: "iOS", value: PlanPlatform.IOS },
  { label: "Android", value: PlanPlatform.ANDROID },
];

const PLATFORMS = [
  { ...ALL_OBJECT, label: "Filter by platform" },
  ...Platforms,
];

const STATUSES = [{ ...ALL_OBJECT, label: "Filter by status" }, ...Statuses];

const getStatus = (type: PlanStatus) => {
  switch (type) {
    case PlanStatus.ACTIVE:
      return <Tag color="#009E60">Active</Tag>;

    case PlanStatus.INACTIVE:
      return <Tag color="#f5222d">Inactive</Tag>;

    default:
      return null;
  }
};

const getKind = (kind?: PlanKind) => {
  switch (kind) {
    case PlanKind.FREE:
      return <Tag>Free</Tag>;
    case PlanKind.SUBSCRIPTION:
      return <Tag color="#1677ff">Subscription</Tag>;
    case PlanKind.TOKEN_PACK:
      return <Tag color="#8b0000">Token pack</Tag>;
    default:
      return <Tag>—</Tag>;
  }
};

const Plans = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [searchText, setSearchText] = useState("");
  const [currentPageSize, setPageSize] = useState(20);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanDto | null>(null);
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const plansService = appInject<IPlanService>(DI_TOKENS.plansService);

  const columns = useMemo(
    () =>
      [
        {
          key: "id",
          dataIndex: "id",
          title: "ID",
          width: 300,
        },
        {
          key: "name",
          dataIndex: "name",
          title: "Name",
          width: 150,
        },
        {
          key: "kind",
          dataIndex: "kind",
          title: "Kind",
          width: 130,
          render: getKind,
        },
        {
          key: "tokens",
          dataIndex: "tokens",
          title: "Tokens",
          width: 100,
        },
        {
          key: "description",
          dataIndex: "description",
          title: "Description",
          render: (value: number | null) => (value === null ? "N/A" : value),
          width: 240,
        },
        {
          key: "status",
          dataIndex: "status",
          title: "Status",
          width: 100,
          render: getStatus,
        },
        {
          key: "revenueCatPlatform",
          dataIndex: "revenueCatPlatform",
          title: "Platform",
          render: getPlatform,
          width: 150,
        },
        {
          key: "revenueCatProductId",
          dataIndex: "revenueCatProductId",
          title: "Product Id",
          className: "max-w-[120px]",
          width: 150,
        },
        {
          key: "reveuecatStoreIdentifier",
          dataIndex: "reveuecatStoreIdentifier",
          title: "Store Identifier",
          className: "max-w-[120px]",
          width: 150,
        },
        {
          key: "revenueCatAppId",
          dataIndex: "revenueCatAppId",
          title: "App Id",
          className: "max-w-[120px]",
          width: 150,
        },
        {
          key: "createdAt",
          dataIndex: "createdAt",
          title: "CreatedAt",
          render: (date: Date | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
          width: 200,
        },
        {
          key: "action",
          title: "Actions",
          render: (_: null, plan: PlanDto) => {
            return (
              <Space
                size="middle"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="primary"
                  color="danger"
                  size="small"
                  style={{
                    outline: "none",
                  }}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setIsOpenModal(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{
                    backgroundColor: "#f5222d",
                    borderColor: "#f5222d",
                    outline: "none",
                  }}
                  onClick={() => {
                    setIsAlertModalOpen(true);
                    setSelectedPlan(plan);
                  }}
                >
                  Delete
                </Button>
              </Space>
            );
          },
          width: 180,
        },
      ] as ColumnsType<PlanDto>,
    [],
  );

  const plansQuery = useQuery(
    [
      "download-plans-list",
      page,
      currentPageSize,
      debouncedSearchText,
      status,
      platform,
    ],
    () =>
      plansService.list({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
        status: (status === ALL
          ? undefined
          : (status ?? undefined)) as PlanStatus,
        revenueCatPlatform: (platform === ALL
          ? undefined
          : (platform ?? undefined)) as PlanPlatform,
      }),
    {
      refetchOnMount: true,
    },
  );

  const syncPlansWithRevenuecatQuery = useMutation(
    ["sync-with-revenuecat"],
    () => plansService.syncWithRevenueCat(),
    {
      onSuccess: () => {
        plansQuery.refetch();
      },
    },
  );

  const updatePlanMutation: UseMutationResult<
    PlanDto,
    unknown,
    UpdatePlanDto,
    unknown
  > = useMutation(
    (data: UpdatePlanDto): Promise<PlanDto> => plansService.update(data),
    {
      onSuccess: () => {
        setSelectedPlan(null);
        setIsOpenModal(false);
        plansQuery.refetch();
      },
    },
  );

  const createPlanMutation: UseMutationResult<
    PlanDto,
    unknown,
    PlanDto,
    unknown
  > = useMutation(
    (data: PlanDto): Promise<PlanDto> => plansService.create(data),
    {
      onSuccess: () => {
        setSelectedPlan(null);
        setIsOpenModal(false);
        plansQuery.refetch();
      },
    },
  );

  const deletePlanMutation: UseMutationResult<
    unknown,
    unknown,
    PlanDto,
    unknown
  > = useMutation((data: PlanDto): Promise<any> => plansService.delete(data), {
    onSuccess: () => {
      setSelectedPlan(null);
      setIsAlertModalOpen(false);
      plansQuery.refetch();
    },
  });

  const handleCloseDialog = () => {
    setIsOpenModal(false);
    setSelectedPlan(null);
    deletePlanMutation.reset();
  };

  const handleSaveDialog = (data: PlanDto) => {
    if (selectedPlan?.id) {
      updatePlanMutation.mutate({ ...data, id: selectedPlan.id });
    } else {
      createPlanMutation.mutate(data);
    }
  };

  const handleProceedDialog = () => {
    if (selectedPlan?.id) {
      deletePlanMutation.mutate(selectedPlan);
    }
  };

  const containerRef = useRef<any>();

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if ("offsetHeight" in containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  // Update debounced search text after a delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300); // Adjust debounce delay as needed

    // Clear the timeout if the effect re-runs (when searchText changes)
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  const handleCloseAlertDialog = () => {
    setIsAlertModalOpen(false);
  };

  const getErrorMessage = (error: any) => {
    if (error) {
      if (error instanceof HttpErrorResponse) {
        return error.errors.get("message" as any) as unknown as string;
      }
      return `Unable to delete the plan. Please try again.`;
    }
  };

  const resetFilters = () => {
    setSearchText("");
    setStatus(ALL);
    setPlatform(ALL);
  };

  return (
    <Layout className={styles.tableLayout}>
      {isOpenModal ? (
        <ChangeUserModal
          open={isOpenModal}
          plan={selectedPlan}
          onClose={handleCloseDialog}
          onSave={handleSaveDialog}
          isLoading={updatePlanMutation.isLoading}
          isError={updatePlanMutation.isError}
        />
      ) : (
        <></>
      )}

      {isAlertModalOpen ? (
        <AlertModal
          open={Boolean(isAlertModalOpen)}
          description={`Are you sure you want to delete the "${selectedPlan?.name}" plan?`}
          onClose={handleCloseAlertDialog}
          isLoading={deletePlanMutation.isLoading}
          error={getErrorMessage(deletePlanMutation.error)}
          onProceed={handleProceedDialog}
        />
      ) : (
        <></>
      )}

      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={7}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
            height: "100%",
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Plans ({plansQuery.data?.data?.total})
          </Typography.Title>

          <Button
            type="primary"
            size="middle"
            onClick={() => {
              setSelectedPlan(defaultPlan as PlanDto);
              setIsOpenModal(true);
            }}
          >
            Add Plan
          </Button>

          <Button
            type="primary"
            size="middle"
            onClick={() => {
              syncPlansWithRevenuecatQuery.mutate();
            }}
          >
            {syncPlansWithRevenuecatQuery.isLoading
              ? "Syncing..."
              : "Sync with RevenueCat"}
          </Button>
        </Col>
        <Col>
          <Form.Item className="search-input-container">
            <Input
              onChange={handleSearch}
              value={searchText}
              name="search"
              style={{
                minWidth: 200,
              }}
              placeholder="Search by Name, Product Id, and Price etc...."
            />

            <Select
              style={{
                minWidth: 150,
              }}
              options={PLATFORMS}
              value={platform}
              onChange={setPlatform}
            />

            <Select
              style={{
                minWidth: 150,
              }}
              options={STATUSES}
              value={status}
              onChange={setStatus}
            />

            <Button type="primary" onClick={resetFilters}>
              Reset
            </Button>
          </Form.Item>
        </Col>
      </Row>
      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="key"
          dataSource={plansQuery.data?.data?.plans || []}
          onChange={onTableChange}
          columns={columns}
          loading={plansQuery.isFetching}
          pagination={{
            current: page,
            total: plansQuery.data?.data?.total || 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight, x: "max-content" }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Plans;
